<template>
  <DataView
    lazy
    dataKey="id"
    :value="attachments || []"
    :layout="layout"
  >
    <template #empty>
      <div class="flex justify-center">No attachments found.</div>
    </template>

    <template #grid="slotProps">
      <div
        :class="
          displayAsThumbnails
            ? 'grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-2'
            : 'grid grid-cols-12 gap-3'
        "
      >
        <div
          v-for="(item, index) in slotProps.items"
          :key="index"
          :class="
            displayAsThumbnails
              ? 'col-span-1'
              : 'col-span-12 sm:col-span-6 md:col-span-6 xl:col-span-4'
          "
        >
          <div
            :class="
              displayAsThumbnails
                ? 'p-2 border border-surface-200 bg-surface-0 rounded flex flex-col'
                : 'p-3 border border-surface-200 bg-surface-0 rounded flex flex-col'
            "
          >
            <!-- Action buttons above image when in thumbnail mode -->
            <div
              v-if="displayAsThumbnails"
              class="flex justify-between items-center mb-1"
            >
              <Tag
                :value="index + 1"
                size="small"
                severity="contrast"
              />
              <div class="cursor-pointer flex gap-1">
                <Tag
                  v-if="!disableDelete"
                  v-tooltip.top="`Delete`"
                  @click="deleteAttachment(index)"
                  size="small"
                  severity="danger"
                >
                  <i class="pi pi-trash" />
                </Tag>
              </div>
            </div>

            <div class="flex items-start">
              <div
                class="flex items-center justify-center relative w-full"
                v-tooltip.top="item.fileName"
              >
                <!-- Action buttons overlaid on image when NOT in thumbnail mode -->
                <div
                  v-if="!displayAsThumbnails"
                  class="absolute top-2 left-2"
                >
                  <Tag
                    :value="index + 1"
                    size="large"
                    severity="contrast"
                  />
                </div>
                <div
                  v-if="!displayAsThumbnails"
                  class="absolute top-2 right-2"
                >
                  <div class="cursor-pointer">
                    <Tag
                      v-tooltip.top="`Open in new tab`"
                      @click="openInNewTab(item)"
                      size="large"
                      severity="info"
                    >
                      <i class="pi pi-external-link" />
                    </Tag>
                    <Tag
                      v-tooltip.top="`Preview`"
                      class="ml-2"
                      @click="
                        () => {
                          selectedAttachmentObj = item;
                          selectedAttachmentObj.isVisible = true;
                        }
                      "
                      size="large"
                      severity="success"
                    >
                      <i class="pi pi-eye" />
                    </Tag>
                    <Tag
                      v-if="!disableDelete"
                      v-tooltip.top="`Delete`"
                      @click="deleteAttachment(index)"
                      class="ml-2"
                      size="large"
                      severity="danger"
                    >
                      <i class="pi pi-trash" />
                    </Tag>
                  </div>
                </div>
                <div v-if="Helper.isImage({ type: item.media_type })">
                  <div
                    :class="
                      displayAsThumbnails
                        ? 'w-32 h-32 rounded-lg cursor-pointer hover:opacity-80 transition-opacity inline-block'
                        : 'cursor-pointer inline-block'
                    "
                    @click="openImageDialog(item)"
                  >
                    <UseImage
                      :src="getImageDisplayUrl(item, index)"
                      :class="
                        displayAsThumbnails
                          ? 'w-32 h-32 object-cover rounded-lg block'
                          : ''
                      "
                    >
                      <template #loading>
                        <div class="row flex-col">
                          <ProgressSpinner
                            style="width: 50px; height: 50px"
                            strokeWidth="3"
                            fill="transparent"
                            animationDuration=".5s"
                            aria-label="Loading..."
                          />
                          <div class="mt-4 text-xl">Loading...</div>
                        </div>
                      </template>
                      <template #error>
                        <UseImage
                          :src="item.local_uri"
                          :class="
                            displayAsThumbnails
                              ? 'w-32 h-32 object-cover rounded-lg cursor-pointer hover:opacity-80 transition-opacity'
                              : ''
                          "
                        >
                          <template #loading>
                            <div class="row flex-col">
                              <ProgressSpinner
                                style="width: 50px; height: 50px"
                                strokeWidth="3"
                                fill="transparent"
                                animationDuration=".5s"
                                aria-label="Loading..."
                              />
                              <div class="mt-4 text-xl">Loading...</div>
                            </div>
                          </template>
                          <template #error>
                            <div class="flex items-center justify-center">
                              <div class="text-center pt-16">
                                <p class="">Oops Error Occured</p>
                                <p class="pt-3">
                                  Can't preview uploaded attachment
                                </p>
                              </div>
                            </div>
                          </template>
                        </UseImage>
                      </template>
                    </UseImage>
                  </div>
                </div>
                <div v-else>
                  <div class="pt-10 pb-5 overflow-hidden text-ellipsis">
                    <span
                      class="block overflow-hidden text-ellipsis whitespace-nowrap break-words"
                    >
                      {{
                        (item.fileName || item.name || "").length > 40
                          ? (item.fileName || item.name || "").slice(0, 40) +
                            "..."
                          : item.fileName || item.name || ""
                      }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </DataView>
  <SnapShotViewDialog
    :visible="selectedAttachmentObj.isVisible"
    :url="selectedAttachmentObj.url"
    :local_uri="selectedAttachmentObj.local_uri"
    :name="selectedAttachmentObj.fileName"
    :type="selectedAttachmentObj.media_type"
    @update:visible="selectedAttachmentObj.isVisible = $event"
  />
</template>

<script>
  import { defineComponent, watch, ref, onBeforeUnmount } from "vue";
  import Helper from "@/utils/helper";
  import SnapShotViewDialog from "@/components/SnapShotViewDialog.vue";
  import { useUserStore } from "@/stores/userStore";

  export default defineComponent({
    name: "AttachmentsGridComponent",
    components: { SnapShotViewDialog },
    props: {
      attachments: {
        type: Array,
        required: true
      },
      layout: {
        type: String,
        default: "grid"
      },
      disableDelete: {
        type: Boolean,
        default: false
      },
      displayAsThumbnails: {
        type: Boolean,
        default: false
      }
    },
    emits: ["update:attachments"],
    setup(props, { emit }) {
      const selectedAttachmentObj = ref({
        isVisible: false,
        url: "",
        name: "",
        type: ""
      });

      const userStore = useUserStore();

      /** Converted image URLs (SVG -> PNG blob URL) keyed by item.id ?? index */
      const convertedUrlMap = ref(new Map());

      const isSvgAttachment = (item) => {
        const type = (item.media_type || "").toLowerCase();
        const name = (item.fileName || item.name || "").toLowerCase();
        return type === "image/svg+xml" || name.endsWith(".svg");
      };

      const getImageDisplayUrl = (item, index) => {
        const key = item.id ?? index;
        const converted = convertedUrlMap.value.get(key);
        if (converted) return converted;
        return userStore.isOnline ? item.url : item.local_uri;
      };

      watch(
        () => [props.attachments, userStore.isOnline],
        () => {
          const attachments = props.attachments || [];
          attachments.forEach((item, index) => {
            if (!Helper.isImage({ type: item.media_type })) return;
            const key = item.id ?? index;
            const url = userStore.isOnline ? item.url : item.local_uri;
            if (
              !url ||
              !isSvgAttachment(item) ||
              convertedUrlMap.value.has(key)
            )
              return;
            Helper.convertSvgUrlToRasterUrl(url)
              .then((blobUrl) => {
                const m = new Map(convertedUrlMap.value);
                m.set(key, blobUrl);
                convertedUrlMap.value = m;
              })
              .catch(() => {});
          });
        },
        { immediate: true }
      );

      onBeforeUnmount(() => {
        convertedUrlMap.value.forEach((url) => {
          if (url && url.startsWith("blob:")) URL.revokeObjectURL(url);
        });
      });

      // Define methods
      const openInNewTab = (item) => {
        Helper.openLinkInNewTab(userStore.isOnline ? item.url : item.local_uri);
      };

      const deleteAttachment = (index) => {
        props.attachments.splice(index, 1);
        emit("update:attachments", props.attachments);
      };

      const openImageDialog = (item) => {
        selectedAttachmentObj.value = {
          isVisible: true,
          url: item.url,
          local_uri: item.local_uri,
          fileName: item.fileName,
          media_type: item.media_type
        };
      };

      return {
        Helper,
        selectedAttachmentObj,
        openInNewTab,
        deleteAttachment,
        openImageDialog,
        userStore,
        getImageDisplayUrl
      };
    }
  });
</script>
